import { useState, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { API_BASE, REDIS_URL } from "../../config";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { streamChatRetrieval } from "../../utils/chatStreaming";
import "../../styles/chat.css";
import WernerChat from "./WernerChat";
import FeedbackModal from "./FeedbackModal";
import WelcomePopup from "./WelcomePopup";
import { WELCOME_POPUP_CONFIG } from "./welcomePopupConfig";
import Sidebar from "../layout/Sidebar";
import { Trash } from "../icons";

const getFeedbackEndpoint = (searchSpace) =>
  `${API_BASE}/ainstein/feedback/submit${searchSpace ? `?searchSpace=${encodeURIComponent(searchSpace)}` : ""
  }`;
const WELCOME_POPUP_SEEN_KEY_PREFIX = "se_welcomePopupSeen_";

const markWelcomePopupSeen = (space) => {
  if (!space) return;

  try {
    localStorage.setItem(`${WELCOME_POPUP_SEEN_KEY_PREFIX}${space}`, "1");
  } catch {
    // Ignore storage write errors (private mode/quota).
  }
};

const normalizeActionName = (name) =>
  String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-");

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const parseActionResponse = async (res, fallbackText) => {
  if (res.status === 204) {
    return {
      text: fallbackText,
      citations: [],
      functions: [],
    };
  }

  let rawBody = "";
  try {
    rawBody = await res.text();
  } catch {
    rawBody = "";
  }

  if (!rawBody) {
    return {
      text: fallbackText,
      citations: [],
      functions: [],
    };
  }

  let data = rawBody;
  try {
    data = JSON.parse(rawBody);
  } catch {
    data = rawBody;
  }

  if (typeof data === "string") {
    return {
      text: data.trim() || fallbackText,
      citations: [],
      functions: [],
    };
  }

  if (!isPlainObject(data)) {
    return {
      text: fallbackText,
      citations: [],
      functions: [],
    };
  }

  const nestedData = isPlainObject(data.data) ? data.data : null;

  const text =
    [
      data.text,
      data.message,
      data.answer,
      data.response,
      data.content,
      data.result,
      typeof data.data === "string" ? data.data : null,
      nestedData?.text,
      nestedData?.message,
      nestedData?.answer,
      nestedData?.response,
      nestedData?.content,
      nestedData?.result,
    ].find((value) => typeof value === "string" && value.trim())?.trim() ||
    fallbackText;

  const citations =
    (Array.isArray(data.citations) && data.citations) ||
    (Array.isArray(data.links) && data.links) ||
    (Array.isArray(data.Links) && data.Links) ||
    (Array.isArray(nestedData?.citations) && nestedData.citations) ||
    (Array.isArray(nestedData?.links) && nestedData.links) ||
    (Array.isArray(nestedData?.Links) && nestedData.Links) ||
    [];

  const functions =
    (Array.isArray(data.functions) && data.functions) ||
    (Array.isArray(nestedData?.functions) && nestedData.functions) ||
    [];

  return {
    text,
    citations,
    functions,
  };
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const ensureMinimumSpinnerTime = async (startedAt, minimumMs = 800) => {
  const elapsed = Date.now() - startedAt;
  if (elapsed < minimumMs) {
    await wait(minimumMs - elapsed);
  }
};

// Normalizes citations into a list of source path strings, matching the
// backend FeedbackTargetMessage.document_links contract (list[str]).
const normalizeDocumentLinks = (citations) => {
  if (!Array.isArray(citations)) return [];

  return citations
    .map((item) => {
      if (typeof item === "string") {
        return item.replace(/^\/+/, "").trim();
      }

      if (item && typeof item === "object") {
        // New Citation shape: { index, source, title, type, space }.
        // Legacy shapes use path/file/url/href.
        const rawPath =
          item.source || item.path || item.file || item.url || item.href || "";
        let cleanPath = String(rawPath || "").trim();
        if (!cleanPath) return "";

        if (cleanPath.startsWith("download://")) {
          cleanPath = decodeURIComponent(cleanPath.replace(/^download:\/\//, ""));
        }

        return cleanPath.replace(/^\/+/, "").trim();
      }

      return "";
    })
    .filter(Boolean);
};

const mapMessageForFeedback = (message) => ({
  id: message?.id ?? null,
  sender: message?.sender ?? null,
  text: message?.text ?? "",
  timestamp: message?.timestamp ?? null,
  document_links: normalizeDocumentLinks(message?.citations),
});

const createWelcomeAiMessage = (timestamp = Date.now(), userName = "") => ({
  id: uuidv4(),
  sender: "ai",
  text: `Hello${userName ? ` ${userName}` : ""}, this is AInstein! How can I help you? 👋`,
  timestamp,
});

function Chat({ searchSpace, themeMode = "dark", onThemeModeChange }) {
  const { accounts } = useMsal();
  const userName = (() => {
    const raw = accounts[0]?.name || "";
    // Parse "LastName, FirstName (ext)" → "FirstName LastName"
    const match = raw.match(/^([^,]+),\s*([^(]+?)(?:\s*\(.*\))?\s*$/);
    if (match) return `${match[2].trim()} ${match[1].trim()}`;
    return raw;
  })();

  const [messages, setMessages] = useState([]);
  // Tracks the chat id currently shown on screen, so an in-flight stream knows
  // whether its updates still belong to the visible message list.
  const activeChatIdRef = useRef(null);
  const [currentSpace, setCurrentSpace] = useState(searchSpace);
  const [isWelcomePopupOpen, setIsWelcomePopupOpen] = useState(false);
  const [isAwaitingResponse, setIsAwaitingResponse] = useState(false);

  useEffect(() => {
    setCurrentSpace(searchSpace);
  }, [searchSpace]);

  const activeSearchSpace = currentSpace || searchSpace;
  const activeWelcomePopupConfig = WELCOME_POPUP_CONFIG[activeSearchSpace] || null;

  useEffect(() => {
    const hasSeenForSpace = Boolean(
      activeSearchSpace &&
      localStorage.getItem(`${WELCOME_POPUP_SEEN_KEY_PREFIX}${activeSearchSpace}`)
    );

    if (activeWelcomePopupConfig && !hasSeenForSpace) {
      setIsWelcomePopupOpen(true);
    } else {
      setIsWelcomePopupOpen(false);
    }
  }, [activeSearchSpace]);

  const [chatHistory, setChatHistory] = useState(() => {
    const seedChat = () => {
      const now = Date.now();
      return [
        {
          id: now,
          name: `Chat ${dayjs(now).format("DD.MM.YYYY")} - ${dayjs(now).format("HH:mm")}`,
          isActive: true,
          messages: [createWelcomeAiMessage(now, userName)],
          createdAt: now,
          lastMessageAt: now,
        },
      ];
    };

    try {
      const stored = JSON.parse(localStorage.getItem("se_chatHistory") || "[]");
      if (!Array.isArray(stored) || stored.length === 0) return seedChat();

      const normalizedChats = stored.map((chat) => {
        if (!chat?.name) {
          const ts = chat?.createdAt || Date.now();
          return { ...chat, name: `Chat ${dayjs(ts).format("DD.MM.YYYY")} - ${dayjs(ts).format("HH:mm")}` };
        }
        return chat;
      });

      const hasActive = normalizedChats.some((c) => c.isActive);
      if (!hasActive && normalizedChats.length > 0) normalizedChats[0].isActive = true;

      return normalizedChats;
    } catch {
      return seedChat();
    }
  });

  const [disabledActionMessageIds, setDisabledActionMessageIds] = useState({});
  const [feedbackModalState, setFeedbackModalState] = useState({
    isOpen: false,
    targetMessage: null,
  });
  const [feedbackSubmittingMessageIds, setFeedbackSubmittingMessageIds] = useState({});
  const [submittedFeedbackMessageIds, setSubmittedFeedbackMessageIds] = useState({});

  useEffect(() => {
    try {
      // Never persist an in-flight/streaming message: it belongs to a JS
      // context (the fetch/ReadableStream loop) that a reload destroys, so
      // writing it here would strand a spinner that can never complete.
      const persistableHistory = chatHistory.map((chat) => ({
        ...chat,
        messages: (chat.messages || []).filter((msg) => !msg?.isLoading),
      }));
      localStorage.setItem("se_chatHistory", JSON.stringify(persistableHistory));
    } catch { }
  }, [chatHistory]);

  useEffect(() => {
    const activeChat = chatHistory.find((c) => c.isActive);
    activeChatIdRef.current = activeChat?.id ?? null;
    if (activeChat?.messages?.length) {
      setMessages(activeChat.messages);
    } else {
      setMessages([createWelcomeAiMessage(Date.now(), userName)]);
    }
  }, []);

  const buildDownloadUrl = (filePath) => {
    const trimmed = (filePath || "").replace(/^\/+/, "");
    const encoded = trimmed.split("/").map(encodeURIComponent).join("/");
    return `${API_BASE}/ainstein/files/${encoded}`;
  };

  const downloadFile = async (filePath) => {
    try {
      const url = buildDownloadUrl(filePath);

      // const downloadWindow = window.open(url, "_blank");
      // if (downloadWindow) return;
      console.log("Attempting to download file via fetchWithAuth:", url);
      const res = await fetchWithAuth(url, {
        method: "GET",
        headers: { Accept: "application/octet-stream, */*" },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filePath.split("/").pop() || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error("Download failed:", e);
      try {
        window.open(buildDownloadUrl(filePath), "_blank");
      } catch {
        alert("File download failed. Please try again or contact support.");
      }
    }
  };

  const handleContainerClick = (e) => {
    if (e.defaultPrevented) return;

    const anchor = e.target.closest && e.target.closest("a");
    if (!anchor) return;

    const href = anchor.getAttribute("href") || "";
    if (!href.startsWith("download://")) return;

    e.preventDefault();
    let filePath = href.replace("download://", "");
    filePath = decodeURIComponent(filePath);
    downloadFile(filePath);
  };

  const closeWelcomePopup = () => {
    markWelcomePopupSeen(activeSearchSpace);
    setIsWelcomePopupOpen(false);
  };

  const closeFeedbackModal = () => {
    setFeedbackModalState({
      isOpen: false,
      targetMessage: null,
    });
  };

  const openFeedbackModal = (message) => {
    if (!message?.id) return;
    if (submittedFeedbackMessageIds[message.id]) return;

    closeWelcomePopup();

    setFeedbackModalState({
      isOpen: true,
      targetMessage: message,
    });
  };

  const submitFeedback = async ({ feedbackType, feedbackText }) => {
    const targetMessage = feedbackModalState.targetMessage;

    if (!targetMessage?.id) {
      throw new Error("No target message selected.");
    }

    const normalizedType = String(feedbackType || "").trim().toLowerCase();
    const trimmedFeedbackText = String(feedbackText || "").trim();

    if (normalizedType !== "positive" && normalizedType !== "negative") {
      throw new Error("Please select thumbs up or thumbs down.");
    }

    if (normalizedType === "negative" && !trimmedFeedbackText) {
      throw new Error("Text feedback is required for thumbs down.");
    }

    const conversation = messages
      .filter((message) => !message?.isLoading)
      .map(mapMessageForFeedback);

    if (!conversation.length) {
      throw new Error("Conversation data is missing.");
    }

    console.log("Target Message:", targetMessage);

    const payload = {
      conversation,
      target_message: mapMessageForFeedback(targetMessage),
      feedback_type: normalizedType,
      feedback_text: trimmedFeedbackText,
    };

    console.log("Payload", payload);

    setFeedbackSubmittingMessageIds((prev) => ({
      ...prev,
      [targetMessage.id]: true,
    }));

    try {
      const res = await fetchWithAuth(getFeedbackEndpoint(activeSearchSpace), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let errorText = "";
        try {
          errorText = await res.text();
        } catch {
          errorText = "";
        }

        throw new Error(
          errorText?.trim() || `Feedback submission failed: ${res.status} ${res.statusText}`
        );
      }

      setSubmittedFeedbackMessageIds((prev) => ({
        ...prev,
        [targetMessage.id]: true,
      }));

      closeFeedbackModal();
    } finally {
      setFeedbackSubmittingMessageIds((prev) => {
        const next = { ...prev };
        delete next[targetMessage.id];
        return next;
      });
    }
  };

  const handleSend = async (text) => {
    const trimmedText = String(text || "").trim();
    if (!trimmedText || isAwaitingResponse) return;

    setIsAwaitingResponse(true);

    const now = Date.now();
    const userMessageId = uuidv4();
    const userMessage = {
      id: userMessageId,
      sender: "human",
      text: trimmedText,
      timestamp: now,
    };

    const loadingMessage = {
      id: "loading",
      sender: "ai",
      text: "",
      isLoading: true,
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage, loadingMessage]);

    setChatHistory((prev) =>
      prev.map((chat) =>
        chat.isActive
          ? {
            ...chat,
            messages: [...(chat.messages || []), userMessage, loadingMessage],
            lastMessageAt: Date.now(),
          }
          : chat
      )
    );

    const indexName = activeSearchSpace;
    const aiMessageId = uuidv4();

    const historyForBackend = [...messages, userMessage];

    const activeChatId = chatHistory.find((chat) => chat.isActive)?.id ?? null;

    try {
      await streamChatRetrieval({
        question: trimmedText,
        conversationHistory: historyForBackend,
        apiBase: API_BASE,
        redisUrl: REDIS_URL,
        indexName,
        chatId: activeChatId,

        onStatusUpdate: (status) => {
          if (activeChatIdRef.current === activeChatId) {
            setMessages((prev) =>
              prev.map((msg) => (msg.id === "loading" ? { ...msg, text: status } : msg))
            );
          }

          setChatHistory((prev) =>
            prev.map((chat) =>
              chat.id === activeChatId
                ? {
                  ...chat,
                  messages: (chat.messages || []).map((msg) =>
                    msg.id === "loading" ? { ...msg, text: status } : msg
                  ),
                  lastMessageAt: Date.now(),
                }
                : chat
            )
          );
        },

        onTextChunk: (transformed) => {
          const updatedAiMessage = {
            id: aiMessageId,
            sender: "ai",
            text: transformed,
            timestamp: Date.now(),
            citations: [],
            functions: [],
            isLoading: true,
          };

          if (activeChatIdRef.current === activeChatId) {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === "loading" || msg.id === aiMessageId ? updatedAiMessage : msg
              )
            );
          }

          setChatHistory((prev) =>
            prev.map((chat) =>
              chat.id === activeChatId
                ? {
                  ...chat,
                  messages: (chat.messages || []).map((msg) =>
                    msg.id === "loading" || msg.id === aiMessageId ? updatedAiMessage : msg
                  ),
                }
                : chat
            )
          );
        },

        onComplete: ({ text: completeText, citations = [], functions = [] }) => {
          const finalAiMessage = {
            id: aiMessageId,
            sender: "ai",
            text: completeText,
            timestamp: Date.now(),
            citations,
            functions,
            isLoading: false,
          };

          if (activeChatIdRef.current === activeChatId) {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === "loading" || msg.id === aiMessageId ? finalAiMessage : msg
              )
            );
          }

          setChatHistory((prev) =>
            prev.map((chat) =>
              chat.id === activeChatId
                ? {
                  ...chat,
                  messages: (chat.messages || []).map((msg) =>
                    msg.id === "loading" || msg.id === aiMessageId ? finalAiMessage : msg
                  ),
                  lastMessageAt: Date.now(),
                }
                : chat
            )
          );
        },

        onError: (err) => {
          console.error("Chat error:", err);

          if (err?.message?.includes("ERR_HTTP2_PROTOCOL_ERROR")) {
            const errorMsg = "Connection error. Please try again.";
            const errorResponse = {
              id: uuidv4(),
              sender: "ai",
              text: errorMsg,
              timestamp: Date.now(),
            };

            if (activeChatIdRef.current === activeChatId) {
              setMessages((prev) =>
                prev.map((msg) => (msg.id === "loading" ? errorResponse : msg))
              );
            }

            setChatHistory((prev) =>
              prev.map((chat) =>
                chat.id === activeChatId
                  ? {
                    ...chat,
                    messages: (chat.messages || []).map((msg) =>
                      msg.id === "loading" ? errorResponse : msg
                    ),
                    lastMessageAt: Date.now(),
                  }
                  : chat
              )
            );
            return;
          }

          const errorMsg = "AInstein can't respond right now. Please retry shortly.";

          const errorResponse = {
            id: uuidv4(),
            sender: "ai",
            text: errorMsg,
            timestamp: Date.now(),
          };

          if (activeChatIdRef.current === activeChatId) {
            setMessages((prev) =>
              prev.map((msg) => (msg.id === "loading" ? errorResponse : msg))
            );
          }

          setChatHistory((prev) =>
            prev.map((chat) =>
              chat.id === activeChatId
                ? {
                  ...chat,
                  messages: (chat.messages || []).map((msg) =>
                    msg.id === "loading" ? errorResponse : msg
                  ),
                  lastMessageAt: Date.now(),
                }
                : chat
            )
          );
        },
      });
    } catch (err) {
      console.error("Unexpected error:", err);
    } finally {
      setIsAwaitingResponse(false);
    }
  };

  const handleNewChat = () => {
    closeFeedbackModal();

    const now = Date.now();

    const newChat = {
      id: now,
      name: `Chat ${dayjs(now).format("DD.MM.YYYY")} - ${dayjs(now).format("HH:mm")}`,
      isActive: true,
      messages: [createWelcomeAiMessage(now, userName)],
      createdAt: now,
      lastMessageAt: now,
    };

    setChatHistory((prev) => [newChat, ...prev.map((chat) => ({ ...chat, isActive: false }))]);
    activeChatIdRef.current = newChat.id;
    setMessages(newChat.messages);
  };

  const handleChatSelect = (chat) => {
    closeFeedbackModal();

    setChatHistory((prev) => prev.map((c) => ({ ...c, isActive: c.id === chat.id })));

    activeChatIdRef.current = chat.id;
    setMessages(chat.messages || [createWelcomeAiMessage(Date.now(), userName)]);
  };

  const handleDeleteChat = (chatId) => {
    closeFeedbackModal();

    const chatToDelete = chatHistory.find((c) => c.id === chatId);
    if (!window.confirm(`Chat "${chatToDelete?.name}" wirklich löschen?`)) return;

    setChatHistory((prev) => {
      const remaining = prev.filter((c) => c.id !== chatId);

      if (remaining.length === 0) {
        const now = Date.now();
        const newChat = {
          id: now,
          name: `Chat ${dayjs(now).format("DD.MM.YYYY")} - ${dayjs(now).format("HH:mm")}`,
          isActive: true,
          messages: [createWelcomeAiMessage(now, userName)],
          createdAt: now,
          lastMessageAt: now,
        };
        activeChatIdRef.current = newChat.id;
        setMessages(newChat.messages);
        return [newChat];
      }

      const first = remaining[0];
      const next = remaining.map((c, i) => ({ ...c, isActive: i === 0 }));

      activeChatIdRef.current = first.id;
      setMessages(first.messages || [createWelcomeAiMessage(Date.now(), userName)]);

      return next;
    });
  };

  const handleMessageCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const setActionMessageDisabled = (messageId, disabled) => {
    if (!messageId) return;

    setDisabledActionMessageIds((prev) => ({
      ...prev,
      [messageId]: disabled,
    }));
  };

  const clearActionFunctions = (messageId) => {
    if (!messageId) return;

    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId
          ? { ...msg, functions: [] }
          : msg
      )
    );

    setChatHistory((prev) =>
      prev.map((chat) =>
        chat.isActive
          ? {
            ...chat,
            messages: (chat.messages || []).map((msg) =>
              msg.id === messageId
                ? { ...msg, functions: [] }
                : msg
            ),
            lastMessageAt: Date.now(),
          }
          : chat
      )
    );
  };

  const handleFunctionCall = async (
    functionName,
    params = {},
    sourceText = "",
    sourceMessageId = null
  ) => {
    const actionMessageId = uuidv4();
    const normalizedFunctionName = normalizeActionName(functionName);
    let loadingStartedAt = null;

    setActionMessageDisabled(sourceMessageId, true);

    const extractCaseId = () => {
      const fromParams = params?.case_id ?? params?.caseId;
      if (fromParams !== undefined && fromParams !== null && String(fromParams).trim() !== "") {
        return String(fromParams).trim();
      }

      const textToSearch = String(sourceText || "");
      const match = textToSearch.match(/\bcase\s+(\d+)\b/i);
      if (match?.[1]) {
        return match[1];
      }

      return null;
    };

    const pushLoadingAiMessage = (text) => {
      loadingStartedAt = Date.now();

      const loadingAiMessage = {
        id: actionMessageId,
        sender: "ai",
        text,
        timestamp: loadingStartedAt,
        citations: [],
        functions: [],
        isLoading: true,
        loadingVariant: "spinner",
      };

      setMessages((prev) => [...prev, loadingAiMessage]);

      setChatHistory((prev) =>
        prev.map((chat) =>
          chat.isActive
            ? {
              ...chat,
              messages: [...(chat.messages || []), loadingAiMessage],
              lastMessageAt: Date.now(),
            }
            : chat
        )
      );
    };

    const replaceLoadingAiMessage = (text, options = {}) => {
      const {
        isError = false,
        citations = [],
        functions = [],
      } = options;

      const finalAiMessage = {
        id: actionMessageId,
        sender: "ai",
        text,
        timestamp: Date.now(),
        citations,
        functions,
        isLoading: false,
        isError,
      };

      setMessages((prev) => {
        const exists = prev.some((msg) => msg.id === actionMessageId);
        if (!exists) return [...prev, finalAiMessage];
        return prev.map((msg) => (msg.id === actionMessageId ? finalAiMessage : msg));
      });

      setChatHistory((prev) =>
        prev.map((chat) => {
          if (!chat.isActive) return chat;

          const chatMessages = chat.messages || [];
          const exists = chatMessages.some((msg) => msg.id === actionMessageId);

          return {
            ...chat,
            messages: exists
              ? chatMessages.map((msg) =>
                msg.id === actionMessageId ? finalAiMessage : msg
              )
              : [...chatMessages, finalAiMessage],
            lastMessageAt: Date.now(),
          };
        })
      );
    };

    try {
      const isReset =
        normalizedFunctionName === "reset" ||
        normalizedFunctionName === "reset-case";

      const isEscalate =
        normalizedFunctionName === "escalate" ||
        normalizedFunctionName === "escalate-case";

      const isRequestRole =
        normalizedFunctionName === "request-role" ||
        normalizedFunctionName === "role-request";

      if (isReset) {
        const caseId = extractCaseId();
        if (!caseId) throw new Error("Case ID is required");

        pushLoadingAiMessage(`Resetting case ${caseId}`);

        const res = await fetchWithAuth(
          `${API_BASE}/ainstein/in/reset-case?case_id=${encodeURIComponent(caseId)}`,
          { method: "GET" }
        );

        if (!res.ok) {
          throw new Error(`Reset failed: ${res.status} ${res.statusText}`);
        }

        await ensureMinimumSpinnerTime(loadingStartedAt);

        replaceLoadingAiMessage(`Case ${caseId} was reset successfully.`);
        clearActionFunctions(sourceMessageId);
        return;
      }

      if (isEscalate) {
        const caseId = extractCaseId();
        if (!caseId) throw new Error("Case ID is required");

        pushLoadingAiMessage(`Escalating case ${caseId}`);

        const res = await fetchWithAuth(
          `${API_BASE}/ainstein/in/escalate-case?case_id=${encodeURIComponent(caseId)}`,
          { method: "GET" }
        );

        if (!res.ok) {
          throw new Error(`Escalate failed: ${res.status} ${res.statusText}`);
        }

        await ensureMinimumSpinnerTime(loadingStartedAt);

        replaceLoadingAiMessage(`Case ${caseId} was escalated successfully.`);
        clearActionFunctions(sourceMessageId);
        return;
      }

      if (isRequestRole) {
        const payload =
          isPlainObject(params?.data)
            ? params.data
            : isPlainObject(params?.payload)
              ? params.payload
              : params;

        if (!isPlainObject(payload) || Object.keys(payload).length === 0) {
          throw new Error("Request role payload is required");
        }

        pushLoadingAiMessage("Submitting role request");

        // include the active chat id so the backend can remove this conversation's
        // persisted agent state after the request is submitted (stops the button
        // from being offered again / duplicate requests)
        const activeChatId = chatHistory.find((chat) => chat.isActive)?.id ?? null;
        const requestRolePayload = { ...payload, chat_id: activeChatId };

        const requestRoleUrl = `${API_BASE}/ainstein/am/request-role`;
        console.log("Request role URL:", requestRoleUrl);
        console.log("Request role payload:", requestRolePayload);

        const res = await fetchWithAuth(requestRoleUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestRolePayload),
        });

        if (!res.ok) {
          throw new Error(`Request role failed: ${res.status} ${res.statusText}`);
        }

        const actionResponse = await parseActionResponse(
          res,
          "Role request processed successfully."
        );

        await ensureMinimumSpinnerTime(loadingStartedAt);

        replaceLoadingAiMessage(actionResponse.text, {
          citations: actionResponse.citations,
          functions: actionResponse.functions,
        });

        clearActionFunctions(sourceMessageId);
        return;
      }

      if (loadingStartedAt) {
        await ensureMinimumSpinnerTime(loadingStartedAt);
      }

      replaceLoadingAiMessage(`Unknown function: ${functionName}`, {
        isError: true,
      });
      console.warn("Unknown function call:", functionName, params);
    } catch (err) {
      console.error("Function call error:", err);
      setActionMessageDisabled(sourceMessageId, false);

      const fallbackText =
        normalizedFunctionName === "reset" || normalizedFunctionName === "reset-case"
          ? "Reset failed."
          : normalizedFunctionName === "escalate" || normalizedFunctionName === "escalate-case"
            ? "Escalation failed."
            : normalizedFunctionName === "request-role" || normalizedFunctionName === "role-request"
              ? "Role request failed."
              : "Function execution failed.";

      if (loadingStartedAt) {
        await ensureMinimumSpinnerTime(loadingStartedAt);
      }

      replaceLoadingAiMessage(
        `${fallbackText} ${err.message || "Unknown error"}`,
        { isError: true }
      );
    }
  };

  const activeFeedbackTargetId = feedbackModalState.targetMessage?.id;
  const isSubmittingFeedback = Boolean(
    activeFeedbackTargetId && feedbackSubmittingMessageIds[activeFeedbackTargetId]
  );

  return (
    <div className="chat-page" onClick={handleContainerClick}>
      <Sidebar
        topOffsetPx={20}
        searchSpace={activeSearchSpace}
        themeMode={themeMode}
        onThemeModeChange={onThemeModeChange}
        childrenFill
      >

        <div className="chat-sidebar-content">
          <button className="chat-new-btn" onClick={handleNewChat}>
            + New Chat
          </button>

          <div className="chat-nav">
            <h3 className="chat-nav-title">Open Chats</h3>

            {chatHistory.map((chat) => (
              <div
                key={chat.id}
                className={`chat-history-item-wrapper ${chat.isActive ? "is-active" : ""}`}
              >
                <span onClick={() => handleChatSelect(chat)} className="chat-history-name">
                  {chat.name}
                </span>

                <button
                  className="chat-delete-btn"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteChat(chat.id);
                  }}
                  title="Chat löschen"
                  aria-label="Chat löschen"
                >
                  <Trash className="chat-delete-icon" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </Sidebar>

      <div className="chat-main">
        <WernerChat
          messages={messages}
          onSendMessage={handleSend}
          isSendingMessage={isAwaitingResponse}
          onCitationClick={downloadFile}
          onMessageCopy={handleMessageCopy}
          onFunctionCall={handleFunctionCall}
          onFeedbackClick={openFeedbackModal}
          disabledActionMessageIds={disabledActionMessageIds}
          feedbackSubmittingMessageIds={feedbackSubmittingMessageIds}
          submittedFeedbackMessageIds={submittedFeedbackMessageIds}
          disclaimerMessage="AInstein uses AI. Verify responses before making decisions."
          currentSpace={activeSearchSpace}
        />
      </div>

      <WelcomePopup
        isOpen={isWelcomePopupOpen}
        title={activeWelcomePopupConfig?.title}
        message={activeWelcomePopupConfig?.message}
        onClose={closeWelcomePopup}
      />

      <FeedbackModal
        isOpen={feedbackModalState.isOpen}
        targetMessage={feedbackModalState.targetMessage}
        isSubmitting={isSubmittingFeedback}
        onClose={closeFeedbackModal}
        onSubmit={submitFeedback}
      />
    </div>
  );
}

export default Chat;